import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Nav } from "../components/Nav";
import { TemplateArt } from "../components/TemplateArt";
import { Camera, Upload, Send, Loader2 } from "lucide-react";

const COLORS = ["#FFB5A7", "#F9F871", "#A8E6CF", "#FFFFFF", "#C8B6FF", "#FFD6E0"];

export default function Customizer() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef();
  const selfieRef = useRef();

  const [template, setTemplate] = useState(null);
  const [message, setMessage] = useState("Wishing you the happiest day! 🎉");
  const [recipient, setRecipient] = useState("");
  const [sender, setSender] = useState(user?.name || "");
  const [photoUrl, setPhotoUrl] = useState(null);
  const [bg, setBg] = useState("#FFB5A7");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/templates/${templateId}`).then(({ data }) => setTemplate(data)).catch(() => navigate("/templates"));
  }, [templateId, navigate]);

  // Restore a saved draft (e.g. after a logged-out visitor signs in and returns)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("so_draft");
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d && d.templateId === templateId) {
        if (typeof d.message === "string") setMessage(d.message);
        if (typeof d.recipient === "string") setRecipient(d.recipient);
        if (d.sender) setSender(d.sender);
        if (d.bg) setBg(d.bg);
        toast.success("Welcome back — your card is right where you left it.");
      }
      localStorage.removeItem("so_draft");
    } catch { /* ignore */ }
  }, [templateId]);

  const saveDraft = () => {
    try {
      localStorage.setItem("so_draft", JSON.stringify({ templateId, message, recipient, sender, bg }));
    } catch { /* ignore */ }
  };

  const promptAuth = (msg = "Log in or sign up to continue") => {
    saveDraft();
    toast(msg, { description: "It's free — your card is saved, then come right back to finish & send." });
    navigate("/login", { state: { redirect: `/create/${templateId}` } });
  };

  const pickPhoto = (ref) => {
    if (!user) { promptAuth("Log in or sign up to add your photo"); return; }
    ref.current?.click();
  };

  const resizeImage = (file, max = 1200) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > max || height > max) {
        const scale = Math.min(max / width, max / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("resize failed"))), "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode failed")); };
    img.src = url;
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!user) { promptAuth("Log in or sign up to add your photo"); e.target.value = ""; return; }
    setUploading(true);
    let blob;
    try {
      blob = await resizeImage(file, 1200);
    } catch {
      toast.error("Please use a JPG, PNG, or WEBP photo (HEIC isn't supported — pick 'Most Compatible' on iPhone, or take a screenshot).");
      setUploading(false); e.target.value = ""; return;
    }
    try {
      const fd = new FormData();
      fd.append("file", blob, "photo.jpg");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(data.url);
      toast.success("Photo added!");
    } catch (err) {
      if (err.response?.status === 401) promptAuth("Log in or sign up to add your photo");
      else toast.error("Couldn't upload that photo. Please try again.");
    } finally {
      setUploading(false); e.target.value = "";
    }
  };

  const send = async () => {
    if (!user) {
      promptAuth("Log in or sign up to send your greeting");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/greetings", {
        template_id: templateId,
        message,
        recipient_name: recipient,
        sender_name: sender,
        photo_url: photoUrl,
        bg_color: bg,
      });
      navigate(`/send/${data.id}`, { state: { greeting: data } });
    } catch (err) {
      alert(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (!template) {
    return (
      <div className="min-h-screen"><Nav />
        <div className="grid place-items-center py-32"><Loader2 className="w-8 h-8 animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-7xl mx-auto px-5 py-8 grid lg:grid-cols-[400px_1fr] gap-8">
        {/* Controls */}
        <div data-testid="customizer-controls">
          <h1 className="text-2xl sm:text-3xl font-display font-bold">Make it yours</h1>
          <p className="text-[#4A4A4A] mt-1">{template.title} · {template.category}</p>

          <div className="space-y-5 mt-6">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest">To</span>
              <input value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="input-recipient"
                placeholder="Recipient's name"
                className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5 focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" />
            </label>

            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest">Your message</span>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="input-message"
                className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" />
            </label>

            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest">From</span>
              <input value={sender} onChange={(e) => setSender(e.target.value)} data-testid="input-sender"
                placeholder="Your name"
                className="w-full nb-border rounded-xl px-4 py-3 bg-white mt-1.5 focus:outline-none focus:ring-2 focus:ring-[#A8E6CF]" />
            </label>

            <div>
              <span className="text-xs font-bold uppercase tracking-widest">Add a photo</span>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="photo-input" />
              <input ref={selfieRef} type="file" accept="image/*" capture="user" onChange={handleFile} className="hidden" data-testid="selfie-input" />
              <div className="flex gap-3 mt-1.5">
                <button onClick={() => pickPhoto(fileRef)} disabled={uploading} data-testid="upload-photo-btn" className="nb-btn bg-[#A8E6CF] px-4 py-3 flex-1">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />} {photoUrl ? "Change" : "Upload"}
                </button>
                <button onClick={() => pickPhoto(selfieRef)} disabled={uploading} data-testid="selfie-btn" className="nb-btn bg-white px-4 py-3 flex-1">
                  <Camera className="w-5 h-5" /> Selfie
                </button>
              </div>
              {!user && <p className="text-xs text-[#4A4A4A] mt-2">Sign up free to add your selfie or photo.</p>}
            </div>

            <div>
              <span className="text-xs font-bold uppercase tracking-widest">Card color</span>
              <div className="flex gap-2 mt-1.5" data-testid="color-picker">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setBg(c)} data-testid={`color-${c}`}
                    className={`w-10 h-10 rounded-xl border-2 border-[#0A0A0A] transition-transform ${bg === c ? "scale-110 shadow-[3px_3px_0px_#0A0A0A]" : ""}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>

            <button onClick={send} disabled={sending} data-testid="create-send-btn" className="nb-btn bg-[#FFB5A7] w-full py-4 text-lg">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} {user ? "Continue to send" : "Sign up to send"}
            </button>
            {!user && (
              <p className="text-center text-sm text-[#4A4A4A]">
                Free to send — <Link to="/signup" className="font-bold underline">create an account</Link> to send & track your greetings.
              </p>
            )}
          </div>
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-24 h-fit">
          <CardPreview template={template} message={message} recipient={recipient} sender={sender} photoUrl={photoUrl} bg={bg} gradient={template.gradient} />
        </div>
      </div>
    </div>
  );
}

export function CardPreview({ template, message, recipient, sender, photoUrl, bg, gradient }) {
  return (
    <div className="nb-card nb-shadow-lg p-0 overflow-hidden mx-auto max-w-md" data-testid="card-preview" style={{ background: bg }}>
      <div className="relative h-64 border-b-2 border-[#0A0A0A]">
        {template.image_url ? (
          <img src={resolveImg(template.image_url)} alt={template.title} className="w-full h-full object-cover" />
        ) : (
          <TemplateArt gradient={gradient} category={template.category} title={template.title} titleClass="text-3xl" />
        )}
        {photoUrl && (
          <img src={resolveImg(photoUrl)} alt="Your photo"
            className="absolute bottom-3 right-3 w-36 h-36 object-cover rounded-xl border-2 border-[#0A0A0A] shadow-[3px_3px_0px_#0A0A0A]" />
        )}
      </div>
      <div className="p-7 text-center">
        {recipient && <div className="font-display font-bold text-lg mb-2">Dear {recipient},</div>}
        <p className="text-lg leading-relaxed whitespace-pre-wrap text-[#0A0A0A]">{message}</p>
        {sender && <div className="font-display font-bold mt-4">— {sender}</div>}
      </div>
    </div>
  );
}
